// "Add User" on the Users admin page: the button, and the design system's
// Modal with the new user's details. The fields stay native inputs (with the
// design system's look) so the browser still checks the required ones.
import React, { useState } from 'react';
import { Button, FormField, Icon, Modal, Select } from '../ds';
import { Note } from '../components/page';
import api from '../services/api';

const FORM_ID = 'create-user-form';

// The design system Input's look, on a native input.
const inputStyle = {
    width: '100%', boxSizing: 'border-box', height: 40, padding: '0 14px',
    borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)',
    background: 'var(--surface-1)', color: 'var(--text-1)', font: 'inherit', fontSize: 'var(--fs-body-md)',
};

/* A labelled field: the label is also the input's accessible name. */
function Field({ label, required, children }) {
    return (
        <FormField label={label} required={required}>
            {React.cloneElement(children, { 'aria-label': label })}
        </FormField>
    );
}

const UserForm = ({ onSuccess }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        companyName: '',
        role: 'user',
        status: 'active',
        tokenAllocation: {
            amount: 1000,
            expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 30 days from now
        }
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Create a formatted date for expiry
        const expiryDate = new Date(formData.tokenAllocation.expiryDate);

        try {
            const userData = {
                ...formData,
                tokenAllocation: {
                    amount: parseInt(formData.tokenAllocation.amount),
                    expiryDate: expiryDate.toISOString()
                }
            };

            console.log('Sending user data:', userData); // For debugging
            await api.post('/admin/users', userData);

            setIsOpen(false);
            setFormData({
                name: '',
                email: '',
                password: '',
                phoneNumber: '',
                companyName: '',
                role: 'user',
                status: 'active',
                tokenAllocation: {
                    amount: 1000,
                    expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                }
            });
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Error creating user:', err.response?.data);
            setError(err.response?.data?.message || 'Error creating user');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('tokenAllocation.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                tokenAllocation: {
                    ...prev.tokenAllocation,
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Get today's date in YYYY-MM-DD format for min date attribute
    const today = new Date().toISOString().split('T')[0];

    return (
        <>
            <Button icon={<Icon name="plus" />} onClick={() => setIsOpen(true)}>
                Add User
            </Button>

            <Modal
                open={isOpen}
                title="Create New User"
                onClose={() => setIsOpen(false)}
                footer={(
                    <>
                        <Button variant="secondary" onClick={() => setIsOpen(false)} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" form={FORM_ID} disabled={loading}>
                            {loading ? 'Creating...' : 'Create User'}
                        </Button>
                    </>
                )}
            >
                <form id={FORM_ID} onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
                    {error && <Note tone="danger">{error}</Note>}

                    <Field label="Name" required>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} style={inputStyle} required />
                    </Field>

                    <Field label="Email" required>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} style={inputStyle} required />
                    </Field>

                    <Field label="Password" required>
                        <input type="password" name="password" value={formData.password} onChange={handleChange} style={inputStyle} required />
                    </Field>

                    <Field label="Phone Number" required>
                        <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} style={inputStyle}
                            required placeholder="Enter phone number" />
                    </Field>

                    <Field label="Company Name" required>
                        <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} style={inputStyle}
                            required placeholder="Enter company name" />
                    </Field>

                    <Select
                        label="Role"
                        value={formData.role}
                        onChange={(value) => handleChange({ target: { name: 'role', value } })}
                        options={[{ value: 'user', label: 'User' }, { value: 'admin', label: 'Admin' }]}
                    />

                    <Field label="Initial Token Amount" required>
                        <input type="number" name="tokenAllocation.amount" value={formData.tokenAllocation.amount}
                            onChange={handleChange} min="0" style={inputStyle} required />
                    </Field>

                    <Field label="Token Expiry Date" required>
                        <input type="date" name="tokenAllocation.expiryDate" value={formData.tokenAllocation.expiryDate}
                            onChange={handleChange} min={today} style={inputStyle} required />
                    </Field>
                </form>
            </Modal>
        </>
    );
};

export default UserForm;
